import React, { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Menu, Bell, LogOut, Loader2, Coins } from 'lucide-react'
import type { User } from '@/types'
import { useAuthStore } from '@/store/authStore'
import { cn, getAvatarUrl } from '@/utils'
import styles from './Header.module.css'

interface HeaderProps {
  user: User | null
  onToggleMobileMenu: () => void
}

export function Header({ user, onToggleMobileMenu }: HeaderProps) {
  const [isProfileOpen, setIsProfileOpen] = useState(false)
  const { logout } = useAuthStore()
  const navigate = useNavigate()


  const [loggingOut, setLoggingOut] = useState(false)

  const handleLogout = async () => {
    setLoggingOut(true)
    try {
      await logout()
    } catch {
      setLoggingOut(false)
    }
  }

  return (
    <header className={styles.header}>
      <div className={styles.headerContent}>
        {/* Mobile menu button */}
        <button 
          className={styles.mobileMenuBtn}
          onClick={onToggleMobileMenu}
          aria-label="Toggle mobile menu"
        >
          <Menu className="w-5 h-5" />
        </button>

        <div className="flex-1" />

        {/* Right side actions */}
        <div className={styles.rightActions}>
          {/* Credits Button */}
          {user && (
            <button
              className={styles.creditsBtn}
              onClick={() => navigate('/users/plans')}
            >
              <Coins className={styles.creditsBtnIcon} />
              <span className={styles.creditsBtnCount}>{user.credit_balance ?? 0}</span>
              <span className={styles.creditsBtnLabel}>Credits</span>
            </button>
          )}

          {/* Profile dropdown */}
          {user && (
            <div className={styles.profileSection}>
              <button
                onClick={() => setIsProfileOpen(!isProfileOpen)}
                className={styles.profileBtn}
                aria-expanded={isProfileOpen}
                aria-haspopup="true"
              >
                <img
                  src={getAvatarUrl(user.display_name, user.avatar_url)}
                  alt={user.display_name || 'User avatar'}
                  className={styles.profileAvatar}
                />
                <div className={styles.profileInfo}>
                  <p className={styles.profileName}>
                    {user.display_name || user.email}
                  </p>
                  <p className={styles.profileRole}>
                    {user.role}
                  </p>
                </div>
              </button>

              {/* Dropdown menu */}
              {isProfileOpen && (
                <div className={styles.dropdownMenu} role="menu">
                  <div className={styles.dropdownHeader}>
                    <p className={styles.dropdownName}>
                      {user.display_name}
                    </p>
                    <p className={styles.dropdownEmail}>
                      {user.email}
                    </p>
                  </div>

                  <button
                    onClick={handleLogout}
                    className={`${styles.signOutBtn} disabled:opacity-60 disabled:cursor-not-allowed`}
                    disabled={loggingOut}
                    role="menuitem"
                  >
                    {loggingOut ? <Loader2 className="animate-spin" /> : <LogOut />}
                    {loggingOut ? 'SIGNING OUT...' : 'SIGN OUT'}
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </header>
  )
}